/**
 * FallingNotesStage - Visual falling notes component
 */

import React, { useMemo } from "react";
import type { ScoreV2, TempoIndexPoint } from "../types";
import { LANE_NAMES } from "../constants";
import { buildVisibleFallingNotes } from "../noteUtils";

interface FallingNotesStageProps {
  score: ScoreV2;
  tempoIndex: TempoIndexPoint[];
  posMs: number;
  activeMask: number;
  lookaheadMs: number;
  hitWindowMs: number;
  speed?: number;
}

export default function FallingNotesStage(props: FallingNotesStageProps) {
  const { score, tempoIndex, posMs, activeMask, lookaheadMs, hitWindowMs, speed = 1.0 } = props;

  // Speed'e göre lookahead'i ayarla - yavaş oynarken daha uzun lookahead gerekir
  const adjustedLookaheadMs = lookaheadMs / speed;

  const notes = useMemo(
    () =>
      buildVisibleFallingNotes({
        score,
        tempoIndex,
        posMs,
        lookaheadMs: adjustedLookaheadMs,
        pastMs: Math.max(80, Math.min(260, adjustedLookaheadMs * 0.08)),
        hitWindowMs,
      }),
    [score, tempoIndex, posMs, adjustedLookaheadMs, hitWindowMs]
  );

  const STAGE_H = 320;
  const PAD_AREA_H = 80;
  const NOTE_SIZE = 18;
  const LANE_HEADER_H = 28;

  return (
    <div
      style={{
        position: "relative",
        height: `${STAGE_H + LANE_HEADER_H}px`,
        borderRadius: "1rem",
        background: "#0f172a",
        border: "1px solid #334155",
        overflow: "hidden",
      }}
    >
      {/* Lane Headers - 8 columns with names */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: `${LANE_HEADER_H}px`,
          display: "grid",
          gridTemplateColumns: "repeat(8, 1fr)",
          background: "rgba(30,41,59,0.8)",
          borderBottom: "1px solid rgba(51,65,85,0.8)",
        }}
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "#94a3b8",
              borderLeft: i === 0 ? "none" : "1px solid rgba(51,65,85,0.5)",
              textTransform: "uppercase",
              letterSpacing: "0.02em",
            }}
          >
            {LANE_NAMES[i as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7]}
          </div>
        ))}
      </div>

      {/* Lanes - 8 columns */}
      <div
        style={{
          position: "absolute",
          top: LANE_HEADER_H,
          left: 0,
          right: 0,
          bottom: 0,
          display: "grid",
          gridTemplateColumns: "repeat(8, 1fr)",
        }}
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            style={{
              borderLeft: i === 0 ? "none" : "1px solid rgba(51,65,85,0.5)",
              opacity: 0.6,
            }}
          />
        ))}
      </div>

      {/* Notes - 8 lanes side by side */}
      {notes.map((n) => {
        // Convert from 4-col to 8-col: lane directly = column
        const xPct8 = (100 / 8) * (n.lane + 0.5);
        return (
          <div
            key={n.key}
            style={{
              position: "absolute",
              width: NOTE_SIZE,
              height: NOTE_SIZE,
              borderRadius: 999,
              left: `calc(${xPct8}% - ${NOTE_SIZE / 2}px)`,
              top: `${n.y - NOTE_SIZE / 2 + LANE_HEADER_H}px`,
              opacity: n.opacity,
              background: n.isHit
                ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                : "linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)",
              boxShadow: n.isHit
                ? "0 10px 30px rgba(16,185,129,0.55)"
                : "0 10px 30px rgba(59,130,246,0.25)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          />
        );
      })}

      {/* Hit Line */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: `${PAD_AREA_H}px`,
          height: 1,
          background: "rgba(51,65,85,0.9)",
        }}
      />

      {/* Debug Info */}
      <div
        style={{
          position: "absolute",
          top: LANE_HEADER_H + 10,
          left: 10,
          padding: "0.35rem 0.5rem",
          fontSize: "0.72rem",
          color: "#94a3b8",
          background: "rgba(2,6,23,0.55)",
          border: "1px solid rgba(51,65,85,0.7)",
          borderRadius: "0.6rem",
          fontFamily: "monospace",
        }}
      >
        t={Math.round(posMs)}ms · lookAhead={Math.round(lookaheadMs)}ms
      </div>

      {/* Pads - 8 columns in single row */}
      <div
        style={{
          position: "absolute",
          left: 12,
          right: 12,
          bottom: 12,
          height: `${PAD_AREA_H - 24}px`,
          display: "grid",
          gridTemplateColumns: "repeat(8, 1fr)",
          gap: "0.5rem",
          padding: "0.5rem",
          borderRadius: "0.9rem",
          background: "rgba(30,41,59,0.55)",
          border: "1px solid rgba(51,65,85,0.8)",
          backdropFilter: "blur(6px)",
        }}
      >
        {Array.from({ length: 8 }).map((_, i) => {
          const on = (activeMask & (1 << i)) !== 0;
          return (
            <div
              key={i}
              style={{
                borderRadius: "0.6rem",
                border: on ? "2px solid #10b981" : "1px solid rgba(51,65,85,1)",
                background: on
                  ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                  : "rgba(15,23,42,0.85)",
                color: on ? "#fff" : "#94a3b8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                fontSize: "0.85rem",
                boxShadow: on ? "0 8px 20px rgba(16,185,129,0.35)" : "none",
                transform: on ? "scale(1.04)" : "scale(1)",
                transition: "transform 0.08s ease, box-shadow 0.08s ease",
              }}
              title={LANE_NAMES[i as 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7]}
            >
              {i + 1}
            </div>
          );
        })}
      </div>
    </div>
  );
}
